import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

export function BinOpModal({ open, onClose, warehouseId, bins }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [op, setOp] = useState('putaway');
  const [productId, setProductId] = useState('');
  const [binId, setBinId] = useState('');
  const [toBinId, setToBinId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products', 'all'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => {
    if (open) { setOp('putaway'); setProductId(''); setBinId(''); setToBinId(''); setQuantity('1'); setError(null); }
  }, [open]);

  const save = useMutation({
    mutationFn: () => {
      const qty = Number(quantity);
      if (op === 'putaway') return inventoryApi.binPutaway({ productId, binId, quantity: qty });
      if (op === 'pick') return inventoryApi.binPick({ productId, binId, quantity: qty });
      return inventoryApi.binMove({ productId, fromBinId: binId, toBinId, quantity: qty });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'bin-contents', warehouseId] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!productId || !binId || !(Number(quantity) > 0) || (op === 'move' && !toBinId)) { setError(t('bin.error.required')); return; }
    save.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('bin.op.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.confirm')}…` : t('common.confirm')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('bin.op.type')}</span>
          <select className="input" value={op} onChange={(e) => setOp(e.target.value)}>
            <option value="putaway">{t('bin.op.putaway')}</option>
            <option value="pick">{t('bin.op.pick')}</option>
            <option value="move">{t('bin.op.move')}</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('bin.field.product')}</span>
          <select className="input" value={productId} onChange={(e) => setProductId(e.target.value)} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{op === 'move' ? t('bin.field.fromBin') : t('bin.field.bin')}</span>
            <select className="input" value={binId} onChange={(e) => setBinId(e.target.value)} required>
              <option value="">-</option>
              {bins.map((b) => <option key={b.id} value={b.id}>{b.code}{b.label ? ` · ${b.label}` : ''}</option>)}
            </select>
          </label>
          {op === 'move' && (
            <label className="field">
              <span className="field-label">{t('bin.field.toBin')}</span>
              <select className="input" value={toBinId} onChange={(e) => setToBinId(e.target.value)} required>
                <option value="">-</option>
                {bins.filter((b) => b.id !== binId).map((b) => <option key={b.id} value={b.id}>{b.code}{b.label ? ` · ${b.label}` : ''}</option>)}
              </select>
            </label>
          )}
          <label className="field">
            <span className="field-label">{t('bin.field.quantity')}</span>
            <input className="input num" type="number" min="0" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
